import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import AddCutout from "../assets/AddCutout"
import FoilMeasures from "../assets/FoilMeasures"
import LoupeOutlinedIcon from "@mui/icons-material/LoupeOutlined"
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react"
import {
  Area,
  PackedRect,
  SolutionRect,
  Solution,
  ComputedData,
  Orientation,
  Currency,
  Foil,
  WorkerMsgIn,
  SubmitPackage,
} from "../common/Models"
import Ruler from "./Ruler"
import { Space, ViewPort } from "react-zoomable-ui"
import ForbinDocument from "./ForbinDocument"
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak"
import RectDrawer from "./Drawers/RectDrawer"
import PriceBox from "./Boxes/PriceBox"
import "../App.css"
import { getComputedData } from "../common/ForbinResolver"
import COLORS from "../common/FortesColors"
import SpaceDrawer from "./Drawers/SpaceDrawer"
import {
  FortesButton,
  FortesButtonCheckable,
} from "./FortesInputs/FortesButtons"
import DetailBox from "./Boxes/DetailBox"
import useWindowSize from "../windowSize"
import ForbinHeader from "./ForbinHeader"
import { downloadPDF, downloadSVG, generateSVG } from "../common/Export"
import HoldButton from "./FortesInputs/HoldButton"
import {
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material"
import { useAdminContext } from "../AdminContext"
import { AppInfo } from "./AppInfo"

export default function Forbin(): JSX.Element {
  const pricePerM2Const = 339
  const { isAdmin } = useAdminContext()

  const [machineGripMM, setMachineGripMM] = useState(10)
  const [plotrPrice, setPlotrPrice] = useState<[number, number, number]>([
    71, 80, 84.5,
  ])
  const [marginDivisor, setMarginDivisor] = useState(0.65)
  const [priceBoxOpen, setPriceBoxOpen] = useState(false)
  const [packedRects, setPackedRects] = useState<Array<PackedRect>>([])
  const [isComputing, setIsComputing] = useState(false)
  const [docWidth, setDocWidth] = useState(410)
  const [docHeight, setDocHeight] = useState(1000)
  const [spacing, setSpacing] = useState(15)
  const [docBorder, setDocBorder] = useState(10)
  const [allArea, setAllArea] = useState<Area>({ w: 0, h: 0, x: 0, y: 0 })
  const [computedData, setcomputedData] = useState<ComputedData | undefined>(
    undefined
  )
  const [pricePerM2, setPricePerM2] = useState(pricePerM2Const)
  const [viewPort, setViewPort] = useState<ViewPort | undefined>(undefined)
  const [cursorX, setCursorX] = useState(0)
  const [cursorY, setCursorY] = useState(0)
  const [cursorPosX, setCursorPosX] = useState("0")
  const [cursorPosY, setCursorPosY] = useState("0")
  const [selectedRect, setSelectedRect] = useState<SolutionRect | undefined>(
    undefined
  )
  const [detailBoxOpen, setDetailBoxOpen] = useState<boolean>(false)
  const [rulerActive, setRulerActive] = useState(true)
  const [errorTxt, setErrorTxt] = useState("no error")
  const [showSolvingError, setShowSolvingError] = useState(false)
  const [showIneficientSolution, setShowIneficientSolution] = useState(false)
  const [rectDrawerOpened, setRectDrawerOpened] = useState(true)
  const [spaceDrawerOpened, setSpaceDrawerOpened] = useState(false)
  const [currency, setCurrency] = useState<Currency>("CZK")
  const [rateEURToCZK, setRate] = useState<number>(0)
  const [foil, setFoil] = useState<Foil>("LOTUS Foil")
  const [xBounds, setXBounds] = useState<[number, number]>([-200, 1918 + 200])
  const [yBounds, setYBounds] = useState<[number, number]>([0, 0])
  const [showAppInfo, setShowAppInfo] = useState(false)

  const BASICALGOS = ["ColumnRowOptimizer", "GuillotineArea", "GuillotineAvg"]
  const ADVANCEDALGOS = [
    "GuillotineBruteforce100YMin",
    "GuillotineBruteforce10YMin",
    "GuillotineBruteforce10Area",
  ]
  const windowSize = useWindowSize()
  const headerHeight = 80
  const sideBarHeight = windowSize.height - headerHeight

  const workerRef = useRef<Worker | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    // Vytvoření pracovníka pouze při prvním renderu
    workerRef.current = new Worker(
      new URL("../common/WorkerResolver.ts", import.meta.url),
      {
        type: "module",
      }
    )

    // Cleanup funkce pro ukončení pracovníka při odpojení komponenty
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
      }
    }
  }, []) // Prázdné pole znamená, že se efekt spustí pouze jednou

  const docRef = useRef()

  const onPackedRectsCahnge = (rects: Array<PackedRect>) => {
    setPackedRects(rects)
  }

  const recenter = useCallback(() => {
    viewPort &&
      viewPort.camera &&
      viewPort.camera.recenter(
        window.innerWidth / 2,
        window.innerHeight / 2,
        0.35
      )
  }, [viewPort])

  useEffect(() => {
    const replyHandler = (e: MessageEvent<Solution | string>) => {
      setIsComputing(false)
      setPriceBoxOpen(false)
      setDetailBoxOpen(false)

      console.log("from worker received", e.data)
      if (typeof e.data === "string") {
        setErrorTxt(e.data)
        setShowSolvingError(true)
        return
      }
      setDocHeight(e.data.allBox.h)
      setAllArea({ h: e.data.allBox.h, w: docWidth })
      setcomputedData(e.data.computedData)
      recenter()
      const usedSpace =
        e.data.computedData.usedAreaMM2 / e.data.computedData.totalAreaMM2
      if (
        usedSpace < 0.4 &&
        e.data.computedData.rects.length > 0 &&
        BASICALGOS.includes(e.data.bestAlgo)
      ) {
        setShowIneficientSolution(true)
      }

      //new bounds for viewPort
      const elementHeight = (1918 / docWidth) * e.data.allBox.h
      setYBounds([-400, elementHeight + /*1720*/ 4000])
    }

    if (workerRef.current) {
      workerRef.current.addEventListener("message", replyHandler)
    }
    return () => {
      if (workerRef.current) {
        workerRef.current?.removeEventListener("message", replyHandler)
      }
    }
  }, [workerRef, spacing, docWidth, packedRects, recenter])
  //not dependent on bASICALGOS since its constant

  useEffect(() => {
    if (foil === "LOTUS Foil") {
      if (currency === "CZK") {
        setPricePerM2(pricePerM2Const)
      } else {
        setPricePerM2(Math.round((pricePerM2Const / rateEURToCZK) * 100) / 100)
      }
      setDocWidth(410)
    }
  }, [foil]) //Missing dependency on currency and rate since
  // this effect should run only on foil change

  const solve = (algos: Array<string> = BASICALGOS) => {
    console.log("solving w", algos)
    if (!workerRef.current) {
      console.log("Worker is null")
      return
    }
    setIsComputing(true)

    if (spacing < 10) setSpacing(10)
    if (docBorder < 10) setDocBorder(10)
    if (docWidth < 10) setDocWidth(10)

    workerRef.current.postMessage({
      docWidth: Math.max(docWidth, 10),
      docHeight: getMaxDocHeight(
        packedRects,
        Math.max(docBorder, 10),
        Math.max(spacing, 10)
      ),
      spacing: Math.max(spacing, 10),
      docBorder: Math.max(docBorder, 10),
      rects: packedRects,
      pricePerM2: pricePerM2,
      selectedAlgos: algos,
      currency: currency,
      rate: rateEURToCZK,
      foil: foil,
      marginDivisor: marginDivisor,
      machineGripMM: machineGripMM,
      plotrPrice: plotrPrice,
    } as WorkerMsgIn)
  }

  const getMaxDocHeight = (
    rects: Array<PackedRect>,
    border: number,
    space: number
  ) => {
    let maxH = 2 * border + space
    rects.forEach((rect: PackedRect) => {
      maxH += ((rect.h > rect.w ? rect.h : rect.w) + space) * rect.cnt
    })
    return maxH
  }

  const mouseUpdate = (event: MouseEvent<HTMLDivElement>) => {
    if (viewPort === undefined) return

    let pos = viewPort.translateClientXYCoordinatesToVirtualSpace(
      event.clientX,
      event.clientY
    )
    let factor = docWidth / viewPort?.containerWidth
    let percX = (pos.x / docWidth) * factor || 0
    let percY = (pos.y / docHeight) * factor || 0
    let valX = Number(percX * docWidth).toFixed(0)
    let valY = Number(percY * docHeight).toFixed(0)
    if (event.clientX !== cursorX || event.clientY !== cursorY) {
      setCursorX(event.clientX)
      setCursorY(event.clientY)
      setCursorPosX(valX)
      setCursorPosY(valY)
    }
  }

  useEffect(() => {
    solve()
  }, []) //Dependencies is empty, so solve()
  //is called (by this effect) only on mount

  useEffect(() => {
    if (!computedData) return
    const newCompData = getComputedData({
      allBox: computedData.allBox,
      boundingBox: computedData.boundingBox,
      spacing: spacing,
      pricePerM2: pricePerM2,
      unpricedSolutionRects: computedData.rects,
      docBorder: docBorder,
      currency: currency,
      rate: rateEURToCZK,
      foil: foil,
      marginDivisor: marginDivisor,
      machineGripMM: machineGripMM,
      plotrPrice: plotrPrice,
    })
    console.log("recomputing data", computedData, newCompData)
    setcomputedData(newCompData)
  }, [currency]) //docBorder, compData,... dependencies missing bcs i want this
  //effect to run only on currency change

  useEffect(() => {
    fetch("https://api.frankfurter.app/latest?from=EUR&to=CZK")
      .then((res) => res.json())
      .then((data) => {
        setRate(data.rates.CZK)
      })
      .catch((err) => console.error(err))
  }, [])

  useEffect(() => {
    const leftOffset = rectDrawerOpened || spaceDrawerOpened
    const rightOffset = priceBoxOpen || detailBoxOpen

    setXBounds([
      -(leftOffset ? 1850 : 1600),
      1918 + (rightOffset ? 1650 : leftOffset ? 1200 : 1600),
    ])
  }, [rectDrawerOpened, spaceDrawerOpened, priceBoxOpen, detailBoxOpen])

  useEffect(() => {
    if (!viewPort) return
    viewPort.setBounds({ y: yBounds, x: xBounds })
    viewPort.camera.recenter(viewPort.centerX, viewPort.centerY + 20, 0.1)
  }, [xBounds, yBounds, viewPort])

  const changeCurrency = (newCurrency: Currency) => {
    if (newCurrency !== currency) {
      if (newCurrency === "CZK") {
        if (foil === "LOTUS Foil") setPricePerM2(pricePerM2Const)
        else setPricePerM2(Math.round(pricePerM2 * rateEURToCZK * 100) / 100)
      } else {
        setPricePerM2(Math.round((pricePerM2 / rateEURToCZK) * 100) / 100)
      }
    }
    setCurrency(newCurrency)
  }

  const getSubmitPkg = (): SubmitPackage => {
    return {
      foil: {
        width: docWidth,
        height: docHeight,
        border: docBorder,
        spaces: spacing,
        priceCZK:
          currency === "CZK"
            ? pricePerM2
            : Math.round(pricePerM2 * rateEURToCZK * 100) / 100,
      },
      cutouts: {
        packed: packedRects,
        solved: computedData?.solvedRects || [],
      },
      svg:
        svgRef.current && computedData
          ? generateSVG(
              svgRef.current,
              computedData.allBox.w,
              computedData.allBox.h
            )
          : undefined,
    }
  }
  return (
    <>
      <Grid
        sx={{
          zIndex: 12,
          backgroundColor: "white",
          borderBlockEnd: `1px solid ${COLORS.separator}`,
          position: "relative",
        }}
      >
        <ForbinHeader
          height={headerHeight}
          //recalculate={solve}
          exportPDFFn={() => {
            if (svgRef.current && computedData)
              downloadPDF(svgRef.current, computedData, docHeight)
            else
              console.log("SVG or computedData is null", svgRef, computedData)
          }}
          exportSVGFn={() => {
            if (svgRef.current && computedData)
              downloadSVG(
                generateSVG(
                  svgRef.current,
                  computedData.allBox.w,
                  computedData.allBox.h
                )
              )
            else
              console.log("SVG or computedData is null", svgRef, computedData)
          }}
          getSubmitPkg={getSubmitPkg}
        />
      </Grid>
      <Grid container direction={"row"} justifyContent="space-between">
        <Box display="flex" flexDirection="row">
          {" "}
          {/* In a box so Box (1st child) and Grid (2nd child) are on the 
              left and box w/ pricing and detail on the right*/}
          <Box
            display="flex"
            flexDirection="row"
            sx={{ zIndex: 12, overflow: "visible" }}
          >
            <Grid
              container
              sx={{
                backgroundColor: "white",
                zIndex: 11,
                borderRight: `1px solid ${COLORS.separator}`,
              }}
              height={`${sideBarHeight}px`}
              width={"62px"}
              direction={"column"}
              alignItems={"start"}
              justifyContent={"space-between"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FortesButtonCheckable
                  width={"42px"}
                  height={"42px"}
                  checked={rectDrawerOpened}
                  icon={<AddCutout />}
                  onClick={() => {
                    setSpaceDrawerOpened(false)
                    setRectDrawerOpened(!rectDrawerOpened)
                  }}
                  alt={`Your\u00A0cut\u2011outs`}
                />
                <FortesButtonCheckable
                  width={"42px"}
                  height={"42px"}
                  checked={spaceDrawerOpened}
                  icon={<FoilMeasures />}
                  onClick={() => {
                    setRectDrawerOpened(false)
                    setSpaceDrawerOpened(!spaceDrawerOpened)
                  }}
                  alt={`Foil\u00A0measurements`}
                />
              </Box>
              {isAdmin && (
                <FortesButton
                  width={"42px"}
                  height="42px"
                  icon={<InfoOutlined />}
                  onClick={() => {
                    setShowAppInfo(true)
                  }}
                  alt={`App\u00A0info`}
                />
              )}
            </Grid>

            {rectDrawerOpened && (
              <Grid
                height={`${sideBarHeight}px`}
                width={"450px"}
                sx={{
                  backgroundColor: "white",
                  zIndex: 10,
                  borderRight: `1px solid ${COLORS.separator}`,
                }}
              >
                <Box>
                  <RectDrawer
                    rects={packedRects}
                    onChange={onPackedRectsCahnge}
                    onClose={() => setRectDrawerOpened(false)}
                    onRecalculate={() => solve()}
                    maxHeight={sideBarHeight}
                    recalculate={solve}
                  />
                </Box>
              </Grid>
            )}

            {spaceDrawerOpened && (
              <Grid
                height={`${sideBarHeight}px`}
                width={"450px"}
                sx={{
                  backgroundColor: "white",
                  zIndex: 10,
                  borderRight: `1px solid ${COLORS.separator}`,
                }}
              >
                <SpaceDrawer
                  drawerHeight={sideBarHeight}
                  docW={docWidth}
                  spacing={spacing}
                  docBorder={docBorder}
                  pricePerM2={pricePerM2}
                  handleDocBorderChange={setDocBorder}
                  handleDocWChange={setDocWidth}
                  handlePriceChange={setPricePerM2}
                  handleSpacingChange={setSpacing}
                  onClose={() => setSpaceDrawerOpened(false)}
                  onReload={() => solve()}
                  currency={currency}
                  handleCurrencyChange={changeCurrency}
                  foil={foil}
                  handleFoilChange={setFoil}
                  machineGripMM={machineGripMM}
                  handleMachineGripMMChange={setMachineGripMM}
                  plotrPrice={plotrPrice}
                  handlePlotrPriceChange={setPlotrPrice}
                  marginDivisor={marginDivisor}
                  handleMarginDivisorChange={setMarginDivisor}
                />
              </Grid>
            )}
          </Box>
          <Grid>
            <div onMouseMove={mouseUpdate} style={{ flexGrow: "1" }}>
              {rulerActive && viewPort && (
                <>
                  <Ruler
                    orientation={Orientation.VERTICAL}
                    viewPort={viewPort}
                    docWidth={docWidth}
                    docHeight={docHeight}
                    drawerOpened={rectDrawerOpened || spaceDrawerOpened}
                  />
                  <Ruler
                    orientation={Orientation.HORIZONTAL}
                    viewPort={viewPort}
                    docWidth={docWidth}
                    docHeight={docHeight}
                  />
                </>
              )}
              {rulerActive && (
                <>
                  <div
                    className="guideline horizontal"
                    style={{ top: cursorY, zIndex: 10 }}
                  >
                    <span className="pos x">{cursorPosY}</span>
                  </div>
                  <div
                    className="guideline vertical"
                    style={{ left: cursorX, zIndex: 10 }}
                  >
                    <span className="pos y">{cursorPosX}</span>
                  </div>
                </>
              )}

              <Space
                onCreate={(viewPort) => {
                  setViewPort(viewPort)
                  recenter()
                }}
                /*onUpdated={(viewPort) => {}}*/
                style={{
                  zIndex: 0,
                }}
              >
                <Box className="document" ref={docRef}>
                  <ForbinDocument
                    allArea={allArea}
                    computed={computedData}
                    onRectSelected={(selected) => {
                      setSelectedRect(selected)
                      if (
                        selectedRect === undefined &&
                        selected !== undefined
                      ) {
                        setDetailBoxOpen(true)
                        setPriceBoxOpen(false)
                      } else if (selected === undefined) setDetailBoxOpen(false)
                    }}
                    /*                     onSelectedRectChange={(args: {
                      origId: string
                      newX: number
                      newY: number
                      newH: number
                      newW: number
                    }) => {
                      try {
                        var mutated: Array<SolutionRect> | undefined =
                          structuredClone(computedData?.solvedRects)
                      } catch {
                        return
                      }
                      if (mutated === undefined) return
                      let elem = mutated.find(
                        (x: SolutionRect) => x.id === args.origId
                      )
                      if (elem === undefined) return

                      const newVals = {
                        newX: args.newX + spacing + docBorder,
                        newY: args.newY + spacing + docBorder,
                        newH: args.newH - spacing,
                        newW: args.newW - spacing,
                      }
                      if (
                        (newVals.newX > elem.x &&
                          newVals.newX + elem.w + docBorder + spacing >
                            docWidth) ||
                        (newVals.newY > elem.y &&
                          newVals.newY + elem.h + docBorder + spacing >
                            docHeight) ||
                        (newVals.newX < elem.x &&
                          newVals.newX - docBorder - spacing < 0) ||
                        (newVals.newY < elem.y &&
                          newVals.newY - docBorder - spacing < 0)
                      )
                        return

                      if (
                        newVals.newH !== elem.h &&
                        (newVals.newX + newVals.newW + docBorder + spacing >
                          docWidth ||
                          newVals.newY + newVals.newH + docBorder + spacing >
                            docHeight ||
                          newVals.newX - docBorder - spacing < 0 ||
                          newVals.newY - docBorder - spacing < 0)
                      )
                        return

                      elem.x = newVals.newX
                      elem.y = newVals.newY
                      elem.w = newVals.newW
                      elem.h = newVals.newH
                      mutated = mutated.map((el: SolutionRect) => {
                        return {
                          id: el.id,
                          pairId: el.pairId,
                          w: el.w + spacing,
                          h: el.h + spacing,
                          r: el.r,
                          x: el.x - spacing - docBorder,
                          y: el.y - spacing - docBorder,
                        }
                      })
                      var d = manualSolve(
                        docWidth,
                        docHeight,
                        spacing,
                        docBorder,
                        mutated || [],
                        pricePerM2,
                        currency,
                        rateEURToCZK,
                        foil
                      )
                      if (d) {
                        setAllArea(d.allArea)
                        setcomputedData(d.computedData)
                      }
                    }} */
                    svgRef={svgRef}
                  />
                </Box>
              </Space>
              <Dialog
                open={!!showSolvingError}
                onClose={() => {
                  setShowSolvingError(false)
                }}
              >
                <DialogTitle>No solution found</DialogTitle>
                <DialogContent>{errorTxt}</DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setShowSolvingError(false)
                    }}
                  >
                    OK
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={showIneficientSolution}
                onClose={() => {
                  setShowIneficientSolution(false)
                }}
              >
                <DialogTitle>Inefficient solution</DialogTitle>
                <DialogContent>
                  We detected that found solution may be inefficient. Do you
                  want to try to find better solution? (Might take longer to
                  complete)
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setShowIneficientSolution(false)
                    }}
                  >
                    no
                  </Button>
                  <Button
                    onClick={() => {
                      solve(ADVANCEDALGOS)
                      setShowIneficientSolution(false)
                    }}
                  >
                    OK
                  </Button>
                </DialogActions>
              </Dialog>
              <Backdrop
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={!!isComputing}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            </div>
          </Grid>
          <Box>
            <Checkbox
              sx={{
                padding: "0px",
                height: "19px",
                width: "19px",
                zIndex: "11",
                color: COLORS.darkPurple,
                "&.Mui-checked": {
                  color: COLORS.darkPurple,
                },
              }}
              checked={rulerActive}
              onChange={(ev) => {
                setRulerActive(ev.target.checked)
              }}
            />
            <Box
              sx={{
                marginLeft: rulerActive ? "30px" : "10px",
              }}
            >
              <Button
                sx={{
                  height: "54px",
                  width: "56px",
                  minWidth: "54px",
                  color: COLORS.darkPurple,
                  backgroundColor: "white",
                  borderRadius: 99999,
                  border: `1px solid ${COLORS.separator}`,
                  marginTop: "10px",
                  zIndex: 11,
                  position: "relative",
                }}
                onClick={recenter}
              >
                <CenterFocusWeakIcon />
              </Button>

              <Box
                height="108px"
                width="54px"
                sx={{
                  borderRadius: 99999,
                  backgroundColor: "white",
                  border: `1px solid ${COLORS.separator}`,
                  marginTop: "10px",
                  zIndex: 11,
                  position: "relative",
                }}
              >
                <HoldButton
                  whileHoldFN={() =>
                    viewPort?.camera.recenter(
                      viewPort.centerX,
                      viewPort.centerY - 20
                    )
                  }
                  repeatTime={10}
                  sx={{
                    height: "54px",
                    width: "54px",
                    zIndex: 11,
                  }}
                >
                  <KeyboardArrowUp />
                </HoldButton>

                <Box
                  sx={{
                    height: "1px",
                    borderTop: `1px solid ${COLORS.separator}`,
                    marginRight: "8px",
                    marginLeft: "8px",
                  }}
                ></Box>

                <HoldButton
                  whileHoldFN={() =>
                    viewPort?.camera.recenter(
                      viewPort.centerX,
                      viewPort.centerY + 20
                    )
                  }
                  repeatTime={10}
                  sx={{
                    height: "54px",
                    width: "54px",
                  }}
                >
                  <KeyboardArrowDown />
                </HoldButton>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box display="flex" flexDirection="row" sx={{ zIndex: 12 }}>
          <Box
            display="flex"
            flexDirection="column"
            sx={{ width: "64px", overflow: "visible" }}
          >
            <Box
              height="64px"
              sx={{
                backgroundColor: "white",
                borderRadius: "20px 0px 0px 20px",
                border: `1px solid ${COLORS.separator}`,
                borderInlineEnd: "0px",
                overflow: "visible",
              }}
            >
              <FortesButtonCheckable
                checked={priceBoxOpen}
                onClick={() => {
                  setPriceBoxOpen(!priceBoxOpen)
                  setDetailBoxOpen(false)
                }}
                icon={<AttachMoneyIcon />}
                alt={`Solution\u00A0price`}
                onRightSide={true}
              />
            </Box>

            {selectedRect && (
              <Box
                height="64px"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "20px 0px 0px 20px",
                  border: `1px solid ${COLORS.separator}`,
                  borderInlineEnd: "0px",
                  overflow: "visible",
                }}
              >
                <FortesButtonCheckable
                  checked={detailBoxOpen}
                  onClick={() => {
                    setDetailBoxOpen(!detailBoxOpen)
                    setPriceBoxOpen(false)
                  }}
                  icon={<LoupeOutlinedIcon />}
                  alt={`Selected\u00A0cut\u2011out\u00A0info`}
                  onRightSide={true}
                />
              </Box>
            )}
          </Box>

          {priceBoxOpen && (
            <Grid
              height={"1000px"}
              width={"450px"}
              sx={{
                borderInlineStart: `1px solid ${COLORS.separator}`,
                backgroundColor: "white",
              }}
            >
              <PriceBox
                totalAreaMM2={computedData?.totalAreaMM2 ?? 0}
                usedAreaMM2={computedData?.usedAreaMM2 ?? 0}
                wasted={computedData?.wasted ?? 0}
                pricePerM2={computedData?.pricePerM2 ?? 0}
                priceStats={computedData?.priceStats ?? {}}
                currency={currency}
                lengthMM={docHeight}
                onCurrencyChange={changeCurrency}
              />
            </Grid>
          )}
          {detailBoxOpen && selectedRect && (
            <Grid
              height={"1000px"}
              width={"450px"}
              sx={{
                borderInlineStart: `1px solid ${COLORS.separator}`,
                backgroundColor: "white",
              }}
            >
              <DetailBox
                rect={selectedRect}
                allBox={computedData!.allBox}
                pricePerRect={
                  computedData?.priceStats[selectedRect.pairId]?.one
                }
                currency={currency}
                spacing={computedData?.spacing || 0}
                border={docBorder}
              />
            </Grid>
          )}
        </Box>
      </Grid>
      {showAppInfo && <AppInfo close={() => setShowAppInfo(false)} />}
    </>
  )
}
